import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import Section from '../../components/Main/Section/Section';
import { CurrentViewContext } from '../../providers/CurrentViewProvider';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { GMBAuthContext } from '../../providers/GMBAuthProvider';
import CommunityManagementConfigurator from '../../components/ReviewMonitoringGuide/CommunityManagementConfigurator';
import { CommunityManagement } from '../../shared/interfaces/ICommunityManagement';
import UserService from '../../services/UserService';
import AlertConfigurator from '../../components/ReviewMonitoringGuide/AlertConfigurator';
import { AlertManagement } from '../../shared/interfaces/IAlerts';
import AlertService from '../../services/AlertService';
import { AuthContext } from '../../providers/AuthProvider';
import GMBService from '../../services/GMBService';
import AuthService from '../../services/AuthService';
import UserSearchWithImpersonate from '../../components/Impersonate/UserSearchWithImpersonate';
import TrustpilotTokenService, {
  failureMessageKey,
  isProgressStep,
  progressMessageKey,
  progressNumber,
  TRUSTPILOT_PROGRESS_TOTAL,
  TrustpilotProgressStep,
  TrustpilotTokenStatus,
} from '../../services/TrustpilotTokenService';
import IngestionAlertRecipients from '../../components/Administration/IngestionAlertRecipients';

const defaultAlertSettings: AlertManagement = {
  active: false,
  id: 1,
  interval: 1,
  options: [
    {
      id: 1,
      name: 'DAILY',
    },
    {
      id: 2,
      name: 'WEEKLY',
    },
  ],
  type: 'NEW_CRAWL',
};

const Administration: FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { hasRole } = useContext(AuthContext);
  const { setCurrentView } = useContext(CurrentViewContext);
  const { user, logout } = useContext(GMBAuthContext);
  const [gmbInterval, setGmbInterval] = useState<string>('24h');
  const [sleepMode, setSleepMode] = useState<boolean>(false);
  const [comLoading, setComLoading] = useState<boolean>(false);
  const [alertLoading, setAlertLoading] = useState<boolean>(false);
  const [newAlertSettings, setNewAlertSettings] = useState<AlertManagement>(defaultAlertSettings);
  const [updatedAlertSettings, setUpdatedAlertSettings] =
    useState<AlertManagement>(defaultAlertSettings);
  const [communityManagementSettings, setCommunityManagementSettings] =
    useState<CommunityManagement>({
      scoreThreshold: 5,
      languagePreference: 'de',
      salutationPrefix: '',
      useUsername: true,
      limitReplyTo400Characters: false,
      greeting: '',
      additionalPrompt: '',
      tonality: {
        preference: 0,
      },
      pronoun: {
        preference: 0,
      },
    });
  const [tpTokenStatus, setTpTokenStatus] = useState<TrustpilotTokenStatus | null>(null);
  const [tpModalOpen, setTpModalOpen] = useState(false);
  const [tpStep, setTpStep] = useState<'email' | 'connecting' | 'code'>('email');
  const tpPollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [tpEmail, setTpEmail] = useState('');
  const [tpCode, setTpCode] = useState('');
  const [tpSessionId, setTpSessionId] = useState('');
  const [tpReference, setTpReference] = useState('');
  const [tpProgressStep, setTpProgressStep] = useState<TrustpilotProgressStep>();
  const [tpLoading, setTpLoading] = useState(false);
  const [tpError, setTpError] = useState('');

  useEffect(() => {
    if (user) {
      setGmbInterval(user.interval);
    }
  }, [user, setGmbInterval]);

  useEffect(() => {
    if (hasRole('can-manage-trustpilot-token')) {
      TrustpilotTokenService.getStatus().then(setTpTokenStatus);
    }
  }, []);

  useEffect(() => {
    setCurrentView(t('nav_administration'));
    document.title = `${t('app_branding')} ${t('nav_administration')}`;
  }, [setCurrentView, t]);

  const saveCommunityManagementSettings = async () => {
    if (!communityManagementSettings) return;
    await UserService.saveCommunityManagementSettings(communityManagementSettings, setComLoading);
  };

  const saveSleep = async () => {
    if (!communityManagementSettings) return;
    await UserService.saveUserSettings(
      { sleeping: sleepMode, communityManagement: communityManagementSettings },
      setComLoading
    );
  };

  const handleGmbIntervalUpdateSelectChange = async (ev: any) => {
    setGmbInterval(ev.target.value);
  };

  const saveGmbIntervalUpdate = async () => {
    await GMBService.setInterval(gmbInterval);
  };

  const saveAlertSettings = async () => {
    if (newAlertSettings && updatedAlertSettings) {
      await AlertService.saveAlertSettings(newAlertSettings, updatedAlertSettings, setAlertLoading);
    }
  };

  const pollTpStatus = (sessionId: string) => {
    const poll = async () => {
      try {
        const resp = await TrustpilotTokenService.getSessionStatus(sessionId);
        // A step past the connecting phase (code_requested) keeps the last progress line until the dialog moves on.
        if (isProgressStep(resp.step)) {
          setTpProgressStep(resp.step);
        }
        if (resp.status === 'code_sent') {
          setTpStep('code');
        } else if (resp.status === 'error') {
          setTpError(t(failureMessageKey(resp.reason)));
          setTpStep('email');
        } else {
          tpPollRef.current = setTimeout(poll, 2000);
        }
      } catch {
        setTpError(t('trustpilot_token_error_start'));
        setTpStep('email');
      }
    };
    poll();
  };

  const handleTpStart = async () => {
    if (!tpEmail) return;
    setTpLoading(true);
    setTpError('');
    try {
      const resp = await TrustpilotTokenService.startSession(tpEmail);
      setTpSessionId(resp.sessionId);
      setTpReference(resp.reference);
      setTpProgressStep(undefined);
      setTpStep('connecting');
      pollTpStatus(resp.sessionId);
    } catch (e: any) {
      setTpError(t('trustpilot_token_error_start'));
    } finally {
      setTpLoading(false);
    }
  };

  const handleTpSubmit = async () => {
    if (!tpCode) return;
    setTpLoading(true);
    setTpError('');
    try {
      const resp = await TrustpilotTokenService.submitCode(tpSessionId, tpCode);
      setTpTokenStatus({
        present: true,
        email: resp.email,
        consumerId: resp.consumerId,
        expiresAt: resp.expiresAt,
      });
      setTpModalOpen(false);
      resetTpModal();
    } catch (e: any) {
      if (e?.response?.status === 410) {
        setTpError(t('trustpilot_token_session_expired'));
        setTpStep('email');
        setTpSessionId('');
        setTpCode('');
      } else {
        // The backend ends the session after any failed submit, so the user has to start over.
        setTpError(t(failureMessageKey(e?.response?.data?.data?.reason)));
        setTpStep('email');
        setTpSessionId('');
        setTpCode('');
      }
    } finally {
      setTpLoading(false);
    }
  };

  const resetTpModal = () => {
    if (tpPollRef.current) {
      clearTimeout(tpPollRef.current);
      tpPollRef.current = null;
    }
    setTpStep('email');
    setTpEmail('');
    setTpCode('');
    setTpSessionId('');
    setTpReference('');
    setTpProgressStep(undefined);
    setTpError('');
    setTpLoading(false);
  };

  useEffect(() => {
    const controller = new AbortController();
    const getCommunityManagementSettings = async () => {
      const response = await UserService.getCommunityManagementSettings(controller.signal);

      if (!response) return;
      if (!response.communityManagement) return;
      setCommunityManagementSettings(response.communityManagement);
      setSleepMode(response.sleeping);
    };

    (async () => await getCommunityManagementSettings())();

    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    updatedAlertSettings.id = 2;
    updatedAlertSettings.type = 'UPDATED_CRAWL';

    const fetchAlertSettings = async () => {
      const response = await AlertService.getAlertSettings(controller.signal);

      if (!response) return;

      if (response.alerts[0]) {
        setNewAlertSettings(response.alerts[0]);
      }

      if (response.alerts[1]) {
        setUpdatedAlertSettings(response.alerts[1]);
      }
    };

    fetchAlertSettings();

    return () => {
      controller.abort();
    };
  }, []);

  return (
    <Section>
      {user && (
        <>
          <Typography variant="h5">{t('account')}</Typography>
          <Divider />
          <Box>
            <Typography
              variant="h6"
              sx={{ mt: theme.spacing(2) }}
            >
              Google
            </Typography>
            <Box sx={{ mt: theme.spacing(1) }}>
              {user.picture && (
                <img
                  src={user.picture}
                  width="64"
                  referrerPolicy="no-referrer"
                  alt="Google avatar"
                />
              )}
              {user.name && <Typography variant="body2">{user.name}</Typography>}
              {user.email && <Typography variant="body2">{user.email}</Typography>}
            </Box>
            <Box sx={{ mt: theme.spacing(1) }}>
              <Button
                variant="contained"
                onClick={async () => await logout()}
              >
                {t('logout')}
              </Button>
            </Box>
            {hasRole('can-set-gmb-interval') && (
              <Box sx={{ maxWidth: 450 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', mt: theme.spacing(2) }}>
                  <FormControl
                    size="small"
                    sx={{ mt: theme.spacing(1) }}
                  >
                    <InputLabel id="gmb-update-interval">{t('gmb_update_interval')}</InputLabel>
                    <Select
                      labelId="gmb-update-interval"
                      value={gmbInterval}
                      onChange={handleGmbIntervalUpdateSelectChange}
                      label={t('gmb_update_interval')}
                    >
                      <MenuItem value="12h">12h</MenuItem>
                      <MenuItem value="24h">24h</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Button
                  variant="contained"
                  sx={{ mt: theme.spacing(1) }}
                  onClick={async () => await saveGmbIntervalUpdate()}
                >
                  {t('save')}
                </Button>
              </Box>
            )}
          </Box>
        </>
      )}

      {(hasRole('can-use-ai') || hasRole('can-use-alerts')) && (
        <Box sx={{ maxWidth: 450 }}>
          {hasRole('can-use-ai') && (
            <>
              <Typography
                variant="h5"
                sx={{ mt: user ? theme.spacing(4) : theme.spacing(0) }}
              >
                {t('community_management')}
              </Typography>
              <Divider />
              <CommunityManagementConfigurator
                settings={communityManagementSettings}
                onChange={setCommunityManagementSettings}
                showKununuReplyLimit
              />
              <Button
                disabled={comLoading}
                variant="contained"
                onClick={async () => await saveCommunityManagementSettings()}
                sx={{ mt: theme.spacing(2) }}
              >
                {t('save')}
              </Button>
            </>
          )}

          {hasRole('can-use-alerts') && (
            <>
              <AlertConfigurator
                newAlert={newAlertSettings}
                updatedAlert={updatedAlertSettings}
                onChangeNew={setNewAlertSettings}
                onChangeUpdated={setUpdatedAlertSettings}
              />
              <Button
                disabled={alertLoading}
                variant="contained"
                onClick={async () => await saveAlertSettings()}
                sx={{ mt: theme.spacing(2) }}
              >
                {t('save')}
              </Button>
            </>
          )}

          {hasRole('can-sleep') && (
            <Box sx={{ maxWidth: 450 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', mt: theme.spacing(2) }}>
                <Typography
                  variant="h5"
                  sx={{ mt: user ? theme.spacing(4) : theme.spacing(0) }}
                >
                  {t('sleep_mode')}
                </Typography>
                <Divider />
                <FormControl
                  size="small"
                  sx={{ mt: theme.spacing(1) }}
                >
                  <InputLabel id="sleep-mode">{t('sleep_mode')}</InputLabel>
                  <Select
                    labelId="sleep-mode"
                    value={sleepMode ? 'true' : 'false'}
                    onChange={(ev) => setSleepMode(ev.target.value == 'true')}
                    label={t('sleep_mode')}
                  >
                    <MenuItem value="false">{t('off')}</MenuItem>
                    <MenuItem value="true">{t('on')}</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Button
                variant="contained"
                sx={{ mt: theme.spacing(1) }}
                onClick={async () => await saveSleep()}
              >
                {t('save')}
              </Button>
            </Box>
          )}
        </Box>
      )}
      {hasRole('can-manage-trustpilot-token') && (
        <Box sx={{ maxWidth: 450, mt: theme.spacing(4) }}>
          <Typography variant="h5">{t('trustpilot_token_section')}</Typography>
          <Divider />
          <Box sx={{ mt: theme.spacing(2) }}>
            {tpTokenStatus?.present ? (
              <>
                <Typography variant="body2">
                  {t('trustpilot_token_email')}: {tpTokenStatus.email}
                </Typography>
                <Typography variant="body2">
                  {t('trustpilot_token_expires_at')}:{' '}
                  {tpTokenStatus.expiresAt
                    ? new Date(tpTokenStatus.expiresAt).toLocaleDateString('de-DE')
                    : '-'}
                </Typography>
              </>
            ) : (
              <Typography variant="body2">{t('trustpilot_token_no_token')}</Typography>
            )}
            <Button
              variant="contained"
              sx={{ mt: theme.spacing(1) }}
              onClick={() => {
                resetTpModal();
                setTpModalOpen(true);
              }}
            >
              {t('trustpilot_token_retrieve')}
            </Button>
          </Box>

          <Dialog
            open={tpModalOpen}
            onClose={() => {
              setTpModalOpen(false);
              resetTpModal();
            }}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>{t('trustpilot_token_retrieve')}</DialogTitle>
            <DialogContent>
              {tpStep === 'email' ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                  <Typography variant="body2">{t('trustpilot_token_enter_email_hint')}</Typography>
                  <TextField
                    label={t('trustpilot_token_email')}
                    value={tpEmail}
                    onChange={(e) => setTpEmail(e.target.value)}
                    type="email"
                    size="small"
                    fullWidth
                    disabled={tpLoading}
                  />
                  {tpError && (
                    <Typography
                      color="error"
                      variant="body2"
                    >
                      {tpError}
                    </Typography>
                  )}
                  {tpError && tpReference && (
                    <Typography
                      color="text.secondary"
                      variant="caption"
                    >
                      {t('trustpilot_token_reference', { reference: tpReference })}
                    </Typography>
                  )}
                  <Button
                    variant="contained"
                    onClick={handleTpStart}
                    disabled={tpLoading || !tpEmail}
                  >
                    {tpLoading ? <CircularProgress size={20} /> : t('trustpilot_token_start')}
                  </Button>
                </Box>
              ) : tpStep === 'connecting' ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                  <Typography variant="body2">{t('trustpilot_token_connecting')}</Typography>
                  <Typography
                    color="text.secondary"
                    variant="body2"
                  >
                    {t('trustpilot_token_progress', {
                      current: progressNumber(tpProgressStep),
                      total: TRUSTPILOT_PROGRESS_TOTAL,
                      activity: t(progressMessageKey(tpProgressStep)),
                    })}
                  </Typography>
                  <LinearProgress />
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                  <Typography variant="body2">{t('trustpilot_token_check_email')}</Typography>
                  <Typography
                    color="text.secondary"
                    variant="body2"
                  >
                    {t('trustpilot_token_no_email_hint')}
                  </Typography>
                  <TextField
                    label={t('trustpilot_token_enter_code')}
                    value={tpCode}
                    onChange={(e) => setTpCode(e.target.value)}
                    size="small"
                    fullWidth
                    disabled={tpLoading}
                  />
                  {tpError && (
                    <Typography
                      color="error"
                      variant="body2"
                    >
                      {tpError}
                    </Typography>
                  )}
                  <Button
                    variant="contained"
                    onClick={handleTpSubmit}
                    disabled={tpLoading || !tpCode}
                  >
                    {tpLoading ? <CircularProgress size={20} /> : t('trustpilot_token_submit')}
                  </Button>
                  <Typography
                    color="text.secondary"
                    variant="caption"
                  >
                    {t('trustpilot_token_reference', { reference: tpReference })}
                  </Typography>
                  <Button
                    variant="text"
                    onClick={() => {
                      setTpStep('email');
                      setTpSessionId('');
                      setTpError('');
                      setTpCode('');
                    }}
                    disabled={tpLoading}
                  >
                    {t('trustpilot_token_back')}
                  </Button>
                </Box>
              )}
            </DialogContent>
          </Dialog>
        </Box>
      )}
      {hasRole('can-manage-ingestion-alerts') && <IngestionAlertRecipients />}
      <UserSearchWithImpersonate />
    </Section>
  );
};

export default Administration;
